import { CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  Box,
  EmailIcon,
  GKButton,
  GKOtpInput,
  LockIcon,
  PhoneInput,
  Show,
  Typography,
} from "components";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";

// Public OTP endpoints — the client device has no localStorage token, so the
// shared axiosInstance would attach `Authorization: Token null`. Use a bare
// axios call (no auth header) for send-OTP / login instead.
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

interface Props {
  logo: string;
  clientId: string | null;
}

const RESEND_SECONDS = 30;

const IconBadge = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      alignItems: "center",
      bgcolor: "var(--primary-50, #e6f0f3)",
      borderRadius: "50%",
      color: "var(--primary-900)",
      display: "flex",
      height: 56,
      justifyContent: "center",
      mx: "auto",
      width: 56,
    }}
  >
    {children}
  </Box>
);

const Authentication = (props: Props) => {
  const { logo, clientId } = props;

  const form = useFormContext();

  const [hasError, setHasError] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isDoneTyping, setIsDoneTyping] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const countryCode = form.watch("auth.countryCode");
  const phoneNumber = form.watch("auth.phoneNumber");

  const verifyOtp = useMutation({
    mutationKey: ["MF_VERIFY_OTP"],
    mutationFn: (code: string) =>
      publicApi.post(
        `/otp/${form.getValues("auth.countryCode")}-${form.getValues("auth.phoneNumber")}/login/`,
        { otp: code },
      ),
    onSuccess: (response) => {
      form.setValue("isVerified", true);
      form.setValue("token", response?.data?.token);
    },
    onError: () => {
      toast.error("Invalid or expired OTP. Please try again.");
      setOtpCode("");
      setIsDoneTyping(false);
    },
  });

  useEffect(() => {
    if (isDoneTyping) verifyOtp.mutate(otpCode);
  }, [isDoneTyping]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  const sendOtpMutation = useMutation({
    mutationKey: ["MF_SEND_OTP"],
    mutationFn: () =>
      publicApi.post(
        `/otp/${form.getValues("auth.countryCode")}-${form.getValues("auth.phoneNumber")}/send_otp/`,
        { email: "", clientId },
      ),
    onSuccess: () => {
      setOtpSent(true);
      setResendIn(RESEND_SECONDS);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Could not send OTP.");
    },
  });

  const handleChangeNumber = () => {
    setOtpSent(false);
    setOtpCode("");
    setIsDoneTyping(false);
    setResendIn(0);
  };

  const verifying = verifyOtp.isLoading;

  return (
    <Box
      sx={{
        bgcolor: "#F8FAFC",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "var(--white)",
          borderBottom: "1px solid var(--neutral-100)",
          display: "flex",
          px: { xs: 2, sm: 4 },
          py: 2,
        }}
      >
        <img
          src={logo || "/Logos/iconFullLogo.png"}
          alt=""
          style={{ maxHeight: "32px", width: "auto" }}
          onError={({ currentTarget }) => {
            currentTarget.src = "https://gridkey.in/Logos/iconFullLogo.png";
          }}
        />
      </Box>

      {/* Body */}
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flex: 1,
          justifyContent: "center",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Box
          component="form"
          onSubmit={form.handleSubmit(() => sendOtpMutation.mutate())}
          sx={{
            bgcolor: "var(--white)",
            border: "1px solid #E3E8EF",
            borderRadius: "20px",
            boxShadow: "0 4px 24px rgba(16, 24, 40, 0.06)",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            maxWidth: 420,
            p: { xs: "28px 20px", sm: "36px 40px" },
            width: "100%",
          }}
        >
          <IconBadge>
            {otpSent ? (
              <EmailIcon height={26} width={26} />
            ) : (
              <LockIcon height={26} width={26} />
            )}
          </IconBadge>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              color="var(--black-900)"
              fontSize={22}
              fontWeight={700}
              sx={{ lineHeight: 1.3 }}
            >
              {otpSent ? "Enter verification code" : "Verify it's you"}
            </Typography>
            <Typography
              color="var(--neutral-500)"
              fontSize={13}
              fontWeight={500}
              sx={{ lineHeight: 1.5, mt: "8px" }}
            >
              {otpSent ? (
                <>
                  We&apos;ve sent a 4-digit code to{" "}
                  <Typography
                    component="span"
                    color="var(--black-900)"
                    fontSize={13}
                    fontWeight={600}
                  >
                    {countryCode} {phoneNumber}
                  </Typography>
                  . Enter it below to review and authorize your order.
                </>
              ) : (
                "Your advisor has placed a mutual fund order for your approval. Verify your registered mobile number to review and authorize it."
              )}
            </Typography>
          </Box>

          {/* Step 1: phone */}
          <Show.When isTrue={!otpSent}>
            <Controller
              name="auth.phoneNumber"
              control={form.control}
              rules={{
                required: { value: true, message: "Please enter phone number" },
              }}
              render={({ field, fieldState: { error } }) => (
                <PhoneInput
                  {...field}
                  inputLabel="Phone Number"
                  onSelectChange={(event: any) =>
                    form.setValue("auth.countryCode", event)
                  }
                  onTextChange={(event: any) => field.onChange(event)}
                  onTextValue={field.value}
                  requiredField
                  onSelectValue={form.getValues("auth.countryCode")}
                  error={!!error}
                  helperText={!!error && error?.message}
                  hasError={hasError}
                  setHasError={setHasError}
                />
              )}
            />
            <GKButton
              variant="contained"
              type="submit"
              disabled={hasError || !phoneNumber}
              loading={sendOtpMutation.isLoading}
              fullWidth
              size="large"
            >
              Send OTP
            </GKButton>
          </Show.When>

          {/* Step 2: OTP */}
          <Show.When isTrue={otpSent}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  opacity: verifying ? 0.6 : 1,
                  pointerEvents: verifying ? "none" : "auto",
                }}
              >
                <GKOtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  setIsTyping={setIsDoneTyping}
                />
              </Box>

              <Show.When isTrue={verifying}>
                <Box
                  sx={{
                    alignItems: "center",
                    color: "var(--neutral-500)",
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={14} thickness={5} />
                  <Typography fontSize={12} fontWeight={500}>
                    Verifying...
                  </Typography>
                </Box>
              </Show.When>

              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <Typography
                  color="var(--neutral-500)"
                  fontSize={12}
                  fontWeight={500}
                >
                  Didn&apos;t receive the code?
                </Typography>
                {resendIn > 0 ? (
                  <Typography
                    color="var(--neutral-400, #98a2b3)"
                    fontSize={12}
                    fontWeight={600}
                  >
                    Resend in {resendIn}s
                  </Typography>
                ) : (
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => sendOtpMutation.mutate()}
                    color="var(--primary-900)"
                    fontSize={12}
                    fontWeight={600}
                    sx={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      p: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Resend OTP
                  </Typography>
                )}
              </Box>

              <GKButton
                variant="text"
                color="dark"
                startIcon={<ArrowLeftIcon height={14} width={14} />}
                onClick={handleChangeNumber}
                disabled={verifying}
                sx={{
                  alignSelf: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Change mobile number
              </GKButton>
            </Box>
          </Show.When>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid #E3E8EF",
          display: "flex",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          © {dayjs().get("year")} gridkey. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Authentication;
